import React, { Component } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { MdRefresh, MdOutlineExpandMore } from "react-icons/md";
import { AgGridReact } from 'ag-grid-react';
import Utils from "context/utils";
import { sessionStatusCellRenderer } from "../../playground/session/sessionStatusCellRenderer";
import { isSessionOpen } from "../../playground/session/sessionStatus";
import { aliasCellRenderer } from "components/controls/aliasCellRenderer";

// Only an OPEN session accepts new executions -- starting one against a closing/closed/deleting
// session would just 409 at the backend (create_execution requires session.status === "open").
// This dialog therefore lists open sessions only, rather than rendering every session and
// disabling the non-open rows: nothing that can be picked here can ever fail with "session not
// open". Exported so the filtering is tested directly -- AgGridReact does not render meaningfully
// under jsdom (see select.test.js).
export function filterOpenSessions(sessions) {
  if (!sessions) {
    return sessions;
  }
  return sessions.filter(isSessionOpen);
}

export default class SelectSessionModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sessions: null,
      lastSessionId: null,
      loading: false,
      error: null,
      selectedSession: null
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.setState({ sessions: null, selectedSession: null }, () => {
        this.loadSessions(false);
      });
    } else if (!this.props.show && prevProps.show) {
      this.setState({ sessions: null, selectedSession: null });
    }
  }

  loadSessions(more) {
    this.setState({ loading: true, error: null });

    const body = {
      engine: "workflow_blobhub",
      command: "list_sessions"
    };
    
    if (more && this.state.lastSessionId) {
      body["start_session_id"] = this.state.lastSessionId;
    }

    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then(response => {
        const newSessions = response.data.sessions;
        const lastSessionId = response.data.last_session_id;

        this.setState(prevState => ({
          sessions: more ? [...prevState.sessions, ...newSessions] : newSessions,
          lastSessionId: lastSessionId,
          loading: false
        }));
      }, error => {
        this.setState({
          loading: false,
          error: "Failed to fetch sessions: " + error.message
        });
      });
  }

  handleSelectForLaunch() {
    if (this.state.selectedSession) {
      this.props.onSelect(this.state.selectedSession);
    }
  }

  handleSelectionChanged(event) {
    const selectedRows = event.api.getSelectedRows();
    this.setState({ selectedSession: selectedRows.length > 0 ? selectedRows[0] : null });
  }

  render() {
    const openSessions = filterOpenSessions(this.state.sessions);

    return (
      <Modal show={this.props.show} onHide={this.props.onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.error && <Alert variant="warning" className="mb-3">{this.state.error}</Alert>}
          {this.state.loading && <Alert variant="primary" className="mb-3">Loading sessions...</Alert>}

          <div className="ag-theme-quartz" style={{ height: '400px' }}>
            <AgGridReact
              rowData={openSessions}
              columnDefs={[
                { field: "id", headerName: "Session ID", flex: 6, filter: true },
                { field: "created_at", headerName: "Created", flex: 7, sort: "desc",
                  cellRenderer: params => Utils.formatCompactDateTime(params.value)
                },
                { field: "alias", headerName: "Alias", flex: 5, filter: true,
                  cellRenderer: aliasCellRenderer
                },
                { field: "description", headerName: "Description", flex: 8, filter: true,
                  cellRenderer: params => params.value || <span className="text-muted">No description</span>
                },
                { field: "status", headerName: "Status", flex: 4,
                  cellRenderer: sessionStatusCellRenderer
                }
              ]}
              rowSelection="single"
              // WITHOUT THIS THE SELECTION UNDOES ITSELF. `rowData` is a fresh array on every
              // render (it is filtered inline), so with no stable id AG Grid treats each render as
              // a new set of rows and drops the selection — which fires onSelectionChanged with
              // nothing selected, which sets state, which renders again. Clicking a row made it
              // flash selected and go back, and the button never enabled.
              getRowId={(params) => params.data.id}
              onSelectionChanged={(event) => this.handleSelectionChanged(event)}
            />
          </div>

          <Form.Text muted className="mt-2">Only open sessions can run executions.</Form.Text>

          {!this.state.loading && this.state.lastSessionId && (
            <div className="mt-3">
              <Button variant="light" size="sm" onClick={() => this.loadSessions(true)}>
                <MdOutlineExpandMore /> Load More Results
              </Button>
            </div>
          )}

        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Cancel</Button>
          <Button
            variant="success"
            onClick={() => this.handleSelectForLaunch()}
            disabled={!this.state.selectedSession}
          >
            Select Session
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
